import type {
  CreateDeliveryAddress,
  DeliveryAddress,
  GeoCoordinates,
  PostalAddress,
} from '@klareospace/pharos-lib-types';

import { toCountryName } from './country';

/**
 * Pre-PHA-363 persisted delivery address shape: a flat `Partial<PostalAddress>` with an
 * optional `title`, no `location` wrapper, and no `originalAddress`. Unmigrated legacy
 * documents (and documents migration 0001 could not fully reshape) still reach the
 * frontends in this shape.
 */
export type LegacyDeliveryAddress = Partial<PostalAddress> & { title?: string };

/**
 * Formats a postal address as "<houseNumber> <street>, <postalCode> <city>, <country>".
 * Empty or whitespace-only parts are skipped.
 * Returns '' for undefined or empty input.
 */
export function formatPostalAddress(address: Partial<PostalAddress> | undefined): string {
  if (!address) {
    return '';
  }

  const streetPart = [address.houseNumber, address.street]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');

  const cityPart = [address.postalCode, address.city]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');

  const countryPart = address.country?.trim();

  return [streetPart, cityPart, countryPart ? toCountryName(countryPart) : undefined].filter(Boolean).join(', ');
}

/**
 * Resolves the flat `PostalAddress`-shaped fields from a delivery address, tolerant of both
 * the current `DeliveryAddress` shape (returns `location.address`) and the pre-PHA-363
 * legacy flat shape (the object itself IS a `Partial<PostalAddress>`).
 * Returns undefined for undefined input, or when a new-shape document is missing `location`.
 */
export function resolveDeliveryPostalAddress(
  address: DeliveryAddress | LegacyDeliveryAddress | undefined,
): Partial<PostalAddress> | undefined {
  if (!address) {
    return undefined;
  }

  if ('location' in address) {
    return address.location?.address;
  }

  return address;
}

/**
 * Resolves the geocoded coordinates from a delivery address, tolerant of both the current
 * `DeliveryAddress` shape (returns `location.coordinates`) and the pre-PHA-363 legacy flat
 * shape (which carries no `location` wrapper, and therefore no coordinates at all).
 * Returns undefined for undefined input, a legacy flat address, or a new-shape document
 * missing `location` — callers must treat undefined as "no coordinates available".
 */
export function resolveDeliveryCoordinates(
  address: DeliveryAddress | LegacyDeliveryAddress | undefined,
): GeoCoordinates | undefined {
  if (!address) {
    return undefined;
  }

  if ('location' in address) {
    return address.location?.coordinates;
  }

  return undefined;
}

/**
 * Formats a delivery address for display, tolerant of both the current `DeliveryAddress`
 * shape and pre-PHA-363 legacy flat documents. Precedence:
 * 1. `formatPostalAddress` of the resolved postal fields (current corrections live here), when non-empty.
 * 2. Trimmed `originalAddress` (new shape), as fallback when resolved fields format to ''.
 * 3. Trimmed legacy `title` (flat shape), last fallback for unmigrated documents.
 */
export function formatDeliveryAddress(address: DeliveryAddress | LegacyDeliveryAddress | undefined): string {
  if (!address) {
    return '';
  }

  const formattedPostalAddress = formatPostalAddress(resolveDeliveryPostalAddress(address));
  if (formattedPostalAddress) {
    return formattedPostalAddress;
  }

  if ('originalAddress' in address) {
    const trimmedOriginal = address.originalAddress?.trim();
    if (trimmedOriginal) {
      return trimmedOriginal;
    }
  }

  if ('title' in address) {
    const trimmedTitle = address.title?.trim();
    if (trimmedTitle) {
      return trimmedTitle;
    }
  }

  return '';
}

/**
 * Formats an unresolved requested address (`Delivery.requestedAddress` /
 * `CreateDeliveryAddress`: client-submitted raw string or flat postal fields).
 * Precedence: trimmed `full`, when non-empty, else `formatPostalAddress` of the flat fields.
 */
export function formatRequestedAddress(address: CreateDeliveryAddress | undefined): string {
  if (!address) {
    return '';
  }

  const trimmedFull = address.full?.trim();
  if (trimmedFull) {
    return trimmedFull;
  }

  return formatPostalAddress(address);
}

/**
 * Canonical key for originalAddress matching.
 * Lowercases, strips commas and periods, collapses whitespace, and trims.
 * No diacritic folding (deliberate v1 decision).
 */
export function normalizeAddressForMatch(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim();
}
